import React, { SyntheticEvent, useCallback, useEffect, useRef, useState } from 'react';
import throttle from 'lodash/throttle';

import * as ST from './styled';

export interface IAutocomplete {
    value: string,
    onChange: (newValue: string) => void,
    onBlur?: (e: SyntheticEvent) => void,
    onLoadSuggestions: (input: string) => Promise<string[]>,
    placeholder?: string,
    isNotValid?: boolean,
    errorMessage?: string,
    className?: string,
    throttleValue?: number,
    isDisabled?: boolean,
}

const Autocomplete = ({
    value,
    onChange,
    onBlur,
    onLoadSuggestions,
    placeholder,
    isNotValid,
    errorMessage,
    className,
    throttleValue = 500,
    isDisabled = false,
}: IAutocomplete): JSX.Element => {
    const inputRef = useRef<HTMLInputElement>();
    const wrapperRef = useRef();
    const throttled = useRef(throttle(newValue => onLoadSuggestions(newValue), throttleValue));
    const [suggestions, setSuggestions] = useState<string[]>([]);
    const [isShow, setIsShow] = useState(false);
    const [activeIndex, setActiveIndex] = useState<number>();

    const func = (e: MouseEvent) => {
        // @ts-ignore TS2345: Argument of type 'undefined' is not assignable to parameter of type 'EventTarget'.
        const clickInside = e.composedPath().includes(wrapperRef.current);
        if (!clickInside) {
            setIsShow(false);
        }
    };

    useEffect(() => {
        if (wrapperRef.current) {
            document.addEventListener('click', func);

            return () => {
                document.removeEventListener('click', func);
            };
        }
    }, [wrapperRef.current]);

    const fetchSuggestions = useCallback(async(_value) => {
        if (_value.length) {
            const result = await throttled.current(_value);
            if (!result) {
                return;
            }
            setSuggestions(result);
        } else {
            setSuggestions([]);
        }
    }, []);

    useEffect(() => {
        setIsShow(suggestions.length > 0);
    }, [suggestions]);

    const changeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { value } = event.target;
        fetchSuggestions(value);
        onChange(value);
    };

    const suggestionClick = (suggestion: string, index?: number) => {
        onChange(suggestion);
        setActiveIndex(index);
        inputRef.current?.focus();
        setIsShow(false);
    };

    const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.nativeEvent.code === 'ArrowDown') {
            const newIndex = activeIndex === undefined || activeIndex + 1 >= suggestions.length
                ? 0
                : activeIndex + 1;
            setActiveIndex(newIndex);
        }
        if (e.nativeEvent.code === 'ArrowUp') {
            const newIndex = activeIndex === undefined || activeIndex - 1 < 0
                ? suggestions.length - 1
                : activeIndex - 1;
            setActiveIndex(newIndex);
        }
        if (e.nativeEvent.code === 'Enter') {
            if (activeIndex !== undefined) {
                suggestionClick(suggestions[activeIndex]);
            } else {
                onChange(value);
                setIsShow(false);
            }
        }
    };

    const handleOnFocus = () => {
        if (suggestions.length > 0) {
            setIsShow(true);
        }
    };

    // @ts-ignore TS2769: No overload matches this call.
    return <ST.Wrapper className={className} ref={wrapperRef}>
        <ST.InputAutocompleteContainer
            value={value}
            onChange={changeHandler}
            onKeyDown={onKeyDown}
            placeholder={placeholder}
            isNotValid={isNotValid}
            errorMessage={errorMessage}
            ref={inputRef}
            data-test={'autocomplete-input'}
            onFocus={handleOnFocus}
            onBlur={onBlur}
            disabled={isDisabled}
        />
        {isShow && <ST.Suggestions data-test={'autocomplete-suggestions'}>
            {suggestions.map((suggestion, index) => (
                <ST.SuggestionItem
                    key={`SuggestionItem_${suggestion}_${index}`}
                    onClick={() => suggestionClick(suggestion, index)}
                    isActive={index === activeIndex}
                >
                    {suggestion}
                </ST.SuggestionItem>
            ))}
        </ST.Suggestions>}
    </ST.Wrapper>;
};

export default Autocomplete;
